import { FC, useState, useEffect, CSSProperties } from 'react';
import { mainButton } from '@telegram-apps/sdk-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import UIConfig from '@/UIConfig.ts';
import { useTonStore } from '@/store/useTonStore.ts';
import { helpers } from '@/helpers.ts';
import TONShape from '@/components/common/svg/shapes/TONShape.tsx';
import {
  contentStyles,
  usdTextStyles,
  valueTextStyles
} from '@/components/EarnTon/Header/styles.ts';

const MINIMUM_WITHDRAW_TON = 1;

const WithdrawTon: FC = () => {
  useBackButtonToPrevRoute(routes.earnTon, false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [isValidWithdraw, setIsValidWithdraw] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const { balance } = useTonStore();
  const balanceTon = helpers.convertMicrosToTON(balance?.ton_micros) || 0;

  useEffect(() => {
    const preventScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('.modal')) return;
      e.preventDefault();
    };
    window.addEventListener('touchmove', preventScroll, { passive: false });

    mainButton.setParams({
      isVisible: isValidWithdraw,
      isEnabled: isValidWithdraw,
      backgroundColor: '#000000',
      text: t('button.continue')
    });

    return () => {
      mainButton.setParams({
        isVisible: false
      });
      window.removeEventListener('touchmove', preventScroll);
    };
  }, [isValidWithdraw]);

  useEffect(() => {
    mainButton.onClick(handleButtonClick);
    return () => {
      mainButton.offClick(handleButtonClick);
    };
  }, [withdrawAmount]);

  const handleButtonClick = () => {
    navigate(generatePath(routes.withdrawTonDetails), {
      state: { requestedWithdrawAmount: withdrawAmount }
    });
  };

  const validateAmount = (amount: string): boolean => {
    if (!amount) {
      setError('');
      return false;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      setError('');
      return false;
    }

    if (numAmount < MINIMUM_WITHDRAW_TON) {
      setError(t('earn.withdraw.error_minimum', { min: MINIMUM_WITHDRAW_TON }));
      return false;
    }

    if (numAmount > Number(balanceTon)) {
      setError(t('earn.withdraw.error_insufficient'));
      return false;
    }

    setError('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputAmount = e.target.value;

    if (inputAmount.startsWith('.') || inputAmount.startsWith(',')) {
      inputAmount = '0' + inputAmount;
    }

    if (!/^[0-9]{0,3}([.,][0-9]{0,4})?$/.test(inputAmount)) return;

    const normalizedInputAmount = inputAmount.replace(',', '.');
    const isValidAmount = validateAmount(normalizedInputAmount);

    setIsValidWithdraw(isValidAmount);
    setWithdrawAmount(normalizedInputAmount);
  };

  const handleMaxClick = () => {
    if (balanceTon) {
      const isValidAmount = validateAmount(balanceTon);

      setIsValidWithdraw(isValidAmount);
      setWithdrawAmount(balanceTon.toString());
    }
  };

  return (
    <div style={containerStyle}>
      <div style={shapeContainerStyle}>
        <TONShape width={60} height={60} fillColor="#fff" />
      </div>
      <div style={contentStyles}>
        <p style={availableBalanceStyle}>
          {t('earn.withdraw.available_balance')}
        </p>
        <p style={valueTextStyles}>
          {helpers.convertMicrosToTON(balance?.ton_micros)}&nbsp;
          <span style={{ fontWeight: 'normal', fontSize: '20px' }}>TON</span>
        </p>
        <p style={usdTextStyles}>
          ≈ {helpers.convertCentsToUSD(balance?.usd_cents)} USD
        </p>
      </div>
      <div style={inputContainerStyle}>
        {error && <span style={errorTextStyle}>{error}</span>}
        <div style={inputWrapperStyle}>
          <input
            style={{
              ...amountInputTextareaStyle,
              borderColor: error ? '#ff0000' : '#ccc'
            }}
            type="number"
            inputMode="decimal"
            placeholder={t('earn.withdraw_amount_placeholder')}
            value={withdrawAmount}
            onChange={handleChange}
            step="0.0001" // Updated step to allow 4 decimal places
          />
          <button style={maxButtonStyle} onClick={handleMaxClick}>
            Max
          </button>
        </div>
      </div>
      <div style={minWithdrawStyle}>
        {t('earn.min_withdraw', { min: MINIMUM_WITHDRAW_TON })}
      </div>
    </div>
  );
};

// Styles remain unchanged
const containerStyle: CSSProperties = {
  position: 'fixed',
  height: '100vh',
  top: 20,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  background: UIConfig.Colors.BackgroundWhite,
  flexDirection: 'column',
  alignItems: 'center',
  boxSizing: 'border-box'
};

const inputContainerStyle: CSSProperties = {
  position: 'relative',
  width: 'calc(100% - 20px)',
  marginTop: 30
};

const shapeContainerStyle: CSSProperties = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: '#000',
  marginBottom: 10
};

const errorTextStyle: CSSProperties = {
  position: 'absolute',
  top: '-18px',
  left: '10px',
  color: '#ff0000',
  fontSize: '12px'
};

const minWithdrawStyle: CSSProperties = {
  marginTop: '4px',
  fontSize: '12px',
  color: '#888'
};

const inputWrapperStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center'
};

const amountInputTextareaStyle = {
  flex: 1,
  resize: 'none' as const,
  borderRadius: '8px',
  padding: '10px 30px 10px 10px',
  border: '1px solid #ccc',
  wordBreak: 'keep-all' as const,
  outline: 'none',
  fontSize: '15px'
};

const availableBalanceStyle = {
  padding: '0px 0',
  margin: 0,
  color: '#888',
  fontWeight: 'bold',
  fontSize: '12px'
};

const maxButtonStyle: CSSProperties = {
  position: 'absolute',
  right: '15px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#0098EA',
  fontSize: '16px',
  lineHeight: '1',
  padding: '0',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default WithdrawTon;
